import {
  ApiException,
  ConfigClient,
  LoginModel,
  PatientInformationClient,
  PatientSiteAuthenticationClient,
} from "@/api/LumediPublicClient";
import { ref } from "@vue/composition-api";
import { defineStore } from "pinia";
import { setI18nLocale } from "@/plugins/i18n";

export const useAuthStore = defineStore("auth", () => {
  const authCookie = "mwm.patientsite.auth";

  const authClient = new PatientSiteAuthenticationClient();
  const infoClient = new PatientInformationClient();

  /**
   * Applies the patient's stored PreferredLanguageCode (returned by GET /me) to the
   * client-rendered UI, so the site renders in their language without a manual toggle.
   * Best-effort: a failure here shouldn't block login or app startup.
   */
  async function applyPreferredLanguage() {
    try {
      const info = await infoClient.getMe();
      if (info.preferredLanguageCode) {
        setI18nLocale(info.preferredLanguageCode);
      }
    } catch (e) {
      console.warn("Failed to load preferred language", e);
    }
  }

  async function login(username: string, password: string, recaptchaToken: string) {
    try {
      await authClient.login(new LoginModel({ user: username, pass: password, recaptchaToken }));
      await applyPreferredLanguage();
    } catch (e) {
      console.error(e);
      throw (e as ApiException).response ?? "An error occurred. Please try again later.";
    }
  }

  async function logout() {
    document.cookie = `${authCookie}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;secure;samesite=lax`;
    return true;
  }

  async function isLoggedIn() {
    return (Object.fromEntries(document.cookie.split("; ").map((x) => x.split("=")))[authCookie] ?? "") != "";
  }

  const recaptchaSiteKey = ref<string>();

  async function setupRecaptcha() {
    if ((window as any).grecaptcha != undefined) return;

    const configClient = new ConfigClient();

    const siteKey = await configClient.getReCaptchaSiteKey();
    recaptchaSiteKey.value = siteKey;

    const scriptUrl = new URL(`https://www.google.com/recaptcha/api.js`);
    scriptUrl.searchParams.append("render", siteKey);

    const el = document.createElement("script");
    el.setAttribute("src", scriptUrl.toString());
    document.head.appendChild(el);

    return siteKey;
  }

  function invokeRecaptcha(action: string): Promise<string> {
    const grecaptcha = (window as any).grecaptcha;
    return new Promise((resolve) => {
      grecaptcha.ready(() => grecaptcha.execute(recaptchaSiteKey.value, { action }).then(resolve));
    });
  }

  return {
    login,
    logout,
    isLoggedIn,
    applyPreferredLanguage,
    setupRecaptcha,
    invokeRecaptcha,
  };
});
